(() => {
  const SELECTOR = '[data-product-review-summary]';
  const CACHE_PREFIX = 'perfumiarz:product-rating:';
  const CACHE_LIFETIME = 15 * 60 * 1000;

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const replaceToken = (template, token, value) =>
    String(template || '').replace(token, String(value));

  const readEmbeddedRating = (summary) => {
    const source = summary.parentElement?.querySelector('[data-product-review-source]');
    const badge = source?.querySelector('[data-average-rating][data-number-of-reviews]');
    if (!badge) return null;

    const count = toNumber(badge.dataset.numberOfReviews);
    const average = toNumber(badge.dataset.averageRating);
    return { count, average };
  };

  const readCachedRating = (productId) => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(`${CACHE_PREFIX}${productId}`));
      if (!cached || Date.now() - cached.savedAt > CACHE_LIFETIME) return null;

      const count = toNumber(cached.count);
      const average = toNumber(cached.average);
      return { count, average };
    } catch (_error) {
      return null;
    }
  };

  const cacheRating = (productId, rating) => {
    try {
      sessionStorage.setItem(
        `${CACHE_PREFIX}${productId}`,
        JSON.stringify({ ...rating, savedAt: Date.now() })
      );
    } catch (_error) {
      // Storage can be unavailable in private browsing; the live result still renders.
    }
  };

  const fetchRating = async (summary) => {
    const productId = summary.dataset.productId;
    const shopDomain = summary.dataset.shopDomain;
    if (!productId || !shopDomain) return null;

    const params = new URLSearchParams({
      shop_domain: shopDomain,
      platform: 'shopify',
      product_id: productId,
      page: '1',
      per_page: '1',
    });
    const response = await fetch(
      `https://api.judge.me/reviews/reviews_for_widget?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      }
    );

    if (!response.ok) return null;

    const payload = await response.json();
    const count = toNumber(payload.number_of_reviews);
    const average = toNumber(payload.average_rating);
    return { count, average };
  };

  const renderRating = (summary, rating) => {
    if (!rating || rating.count < 1) return;

    const roundedRating = Math.max(0, Math.min(5, Math.round(rating.average)));
    summary.querySelectorAll('[data-product-review-dot]').forEach((dot) => {
      dot.classList.toggle('is-filled', toNumber(dot.dataset.productReviewDot) <= roundedRating);
    });

    const dots = summary.querySelector('[data-product-review-dots]');
    const ratingLabel = replaceToken(
      summary.dataset.ratingLabel,
      '__rating__',
      rating.average.toFixed(1)
    );
    dots?.setAttribute('aria-label', ratingLabel);

    const countText =
      rating.count === 1 ? summary.dataset.countOne : summary.dataset.countOther;
    const count = summary.querySelector('[data-product-review-count]');
    if (count) {
      count.textContent = replaceToken(countText, '__count__', rating.count);
    }

    summary.hidden = false;
  };

  const initializeSummary = async (summary) => {
    if (summary.dataset.productReviewInitialized === 'true') return;
    summary.dataset.productReviewInitialized = 'true';

    const productId = summary.dataset.productId;
    const embedded = readEmbeddedRating(summary);
    if (embedded) {
      renderRating(summary, embedded);
      cacheRating(productId, embedded);
      return;
    }

    const cached = readCachedRating(productId);
    if (cached) {
      renderRating(summary, cached);
      return;
    }

    try {
      const rating = await fetchRating(summary);
      if (!rating) return;
      renderRating(summary, rating);
      cacheRating(productId, rating);
    } catch (_error) {
      // Keep the summary hidden if Judge.me is temporarily unavailable.
    }
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initializeSummary);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
